<template>
  <div class="recent-games-list">
    <!-- Loading State -->
    <div v-if="loading" class="list-loading">
      <span class="material-symbols-outlined spinning">sync</span>
      <span>Loading recent games...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="games.length === 0" class="list-empty">
      <span class="material-symbols-outlined empty-icon">sports_esports</span>
      <p class="empty-title">No recent games</p>
      <p class="empty-hint">Games less than 5 days old will appear here</p>
    </div>

    <!-- Games Header with Delete All -->
    <div v-else class="games-header">
      <span class="games-count">{{ games.length }} recent game{{ games.length !== 1 ? 's' : '' }}</span>
      <button class="delete-all-btn" @click="$emit('delete-all')" title="Delete all recent games">
        <span class="material-symbols-outlined">delete_sweep</span>
        Clear All
      </button>
    </div>

    <!-- Games Grid -->
    <div v-if="games.length > 0" class="games-grid">
      <div
        v-for="game in games"
        :key="game.id"
        class="game-card"
        :class="[game.status, { 'is-disabled': disabled }]"
        :title="`Room: ${game.room_code} • ${formatStatus(game.status)}`"
        @click="!disabled && $emit('resume', game)"
      >
        <!-- Complete stamp overlay for finished games -->
        <div v-if="game.status === 'finished'" class="complete-stamp">
          COMPLETE
        </div>

        <div class="game-scores">
          <div class="team-row" :class="{ winner: game.winning_team === 'A' }">
            <span class="team-name">{{ game.team_a_name }}</span>
            <span class="score">{{ game.team_a_score.toLocaleString() }}</span>
          </div>
          <span class="vs">vs</span>
          <div class="team-row" :class="{ winner: game.winning_team === 'B' }">
            <span class="team-name">{{ game.team_b_name }}</span>
            <span class="score">{{ game.team_b_score.toLocaleString() }}</span>
          </div>
        </div>

        <div class="game-meta">
          <span class="player-info">
            <span class="material-symbols-outlined">groups</span>
            {{ game.human_count }} human{{ game.human_count !== 1 ? 's' : '' }},
            {{ game.bot_count }} bot{{ game.bot_count !== 1 ? 's' : '' }}
          </span>
          <span class="time-info">{{ formatTimeAgo(game.updated_at) }}</span>
        </div>

        <div class="game-actions">
          <button
            class="resume-btn"
            :disabled="disabled"
            @click.stop="!disabled && $emit('resume', game)"
          >
            <span class="material-symbols-outlined" :class="{ spinning: disabled }">{{ disabled ? 'sync' : 'play_arrow' }}</span>
            {{ disabled ? 'Resuming...' : 'Resume Game' }}
          </button>
          <button class="delete-btn" @click.stop="$emit('delete', game)" title="Remove from recent games">
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { GameSummary } from '@/types/game'
import { useGameHistory } from '@/composables/useGameHistory'

defineProps<{
  games: GameSummary[]
  loading: boolean
  disabled?: boolean // True when a resume/join is in progress
}>()

defineEmits<{
  (e: 'resume', game: GameSummary): void
  (e: 'delete', game: GameSummary): void
  (e: 'delete-all'): void
}>()

const { formatTimeAgo, formatStatus } = useGameHistory()
</script>

<style scoped>
.recent-games-list {
  padding: 0;
}

/* Loading State */
.list-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.7);
}

.list-loading .spinning {
  font-size: 2rem;
  color: #e8c547;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Empty State */
.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  color: rgba(232, 197, 71, 0.4);
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 0.5rem 0;
}

.empty-hint {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

/* Games Grid - More compact for better fit */
.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}

/* Game Card - Compact version */
.game-card {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 0.875rem;
  cursor: pointer;
  transition: all 0.25s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.game-card:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(232, 197, 71, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

/* Status-based border colors */
.game-card.in_progress {
  border-left: 3px solid #4ade80;  /* Green - active games */
}

.game-card.waiting {
  border-left: 3px solid #60a5fa;  /* Blue - waiting for players */
}

.game-card.paused {
  border-left: 3px solid #fbbf24;  /* Yellow - paused */
}

.game-card.finished {
  border-left: 3px solid #6b7280;  /* Gray - finished games */
}

/* Dim content for finished games */
.game-card.finished .game-scores,
.game-card.finished .game-meta {
  opacity: 0.6;
}

/* Complete stamp overlay for finished games */
.complete-stamp {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);

  /* Red rectangle with rounded edges */
  background: rgba(220, 38, 38, 0.9);
  border: 3px solid rgba(185, 28, 28, 1);
  border-radius: 8px;
  padding: 6px 16px;

  /* Spray paint / stencil text effect */
  font-family: 'Impact', 'Arial Black', sans-serif;
  font-size: 1rem;
  font-weight: 900;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: white;
  text-shadow:
    2px 2px 0 rgba(0, 0, 0, 0.3),
    -1px -1px 0 rgba(0, 0, 0, 0.2);

  /* Stencil/distressed effect */
  -webkit-text-stroke: 0.5px rgba(255, 255, 255, 0.3);

  /* Ensure it's above other content */
  z-index: 10;
  pointer-events: none;

  /* Slight grunge/worn effect */
  box-shadow:
    inset 0 0 10px rgba(0, 0, 0, 0.3),
    2px 2px 8px rgba(0, 0, 0, 0.4);
}

/* Disabled state while resuming */
.game-card.is-disabled {
  opacity: 0.6;
  pointer-events: none;
  cursor: not-allowed;
}

.game-card.is-disabled:hover {
  transform: none;
  box-shadow: none;
}

/* Scores - Vertical Stack Layout */
.game-scores {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.625rem;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
}

.team-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  min-width: 0;
}

.team-row .team-name {
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-row .score {
  font-size: 0.875rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  margin-left: 0.5rem;
  flex-shrink: 0;
}

.team-row.winner {
  background: rgba(74, 222, 128, 0.1);
}

.team-row.winner .score {
  color: #4ade80;
}

.vs {
  font-size: 0.5625rem;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Meta Info - Compact */
.game-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.625rem;
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.6);
  gap: 0.375rem;
  min-width: 0;
  flex-wrap: wrap;
}

.player-info {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
}

.player-info .material-symbols-outlined {
  font-size: 0.875rem;
  color: rgba(232, 197, 71, 0.6);
}

.time-info {
  font-style: italic;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Games Header */
.games-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
}

.games-count {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
}

.delete-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: #ef4444;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.delete-all-btn:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: #ef4444;
}

.delete-all-btn .material-symbols-outlined {
  font-size: 1rem;
}

/* Game Actions - Compact */
.game-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.375rem;
  margin-top: auto;
  padding-top: 0.375rem;
}

.resume-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, #4ade80, #22c55e);
  border: none;
  border-radius: 6px;
  color: #0a0a0a;
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.resume-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  transform: scale(1.02);
}

.resume-btn:disabled {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  cursor: not-allowed;
  opacity: 0.8;
}

.resume-btn .material-symbols-outlined {
  font-size: 0.875rem;
}

.delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: rgba(239, 68, 68, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: #ef4444;
  color: #ef4444;
}

.delete-btn .material-symbols-outlined {
  font-size: 0.9375rem;
}
</style>
